import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Button,
    Modal,
    Form,
    Select,
    Input,
    InputNumber,
    message,
    Space,
    Tag,
    Typography,
    Divider,
    Progress
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    ShoppingOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BudgetPlanningTab = ({ projectId, projectData, onDataChange }) => {
    const [budgetSummary, setBudgetSummary] = useState(null);
    const [budgetItems, setBudgetItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();

    // 카테고리 정의
    const categories = {
        'outsourcing': { name: '외주비', color: 'blue', icon: <ShoppingOutlined /> },
        'material': { name: '자재비', color: 'green', icon: <FileTextOutlined /> },
        'expense': { name: '경비', color: 'orange', icon: <DollarOutlined /> },
        'other': { name: '기타', color: 'purple', icon: <FileTextOutlined /> }
    };

    // 데이터 로드
    const loadBudgetData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // 예산 요약 조회
            const summaryResponse = await axios.get(
                `${API_BASE}/project-budget/project/${projectId}/summary`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBudgetSummary(summaryResponse.data);

            // 예산 항목 조회
            const itemsResponse = await axios.get(
                `${API_BASE}/project-budget/project/${projectId}/items`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const items = itemsResponse.data.budget_items || [];
            setBudgetItems(items);
            setFilteredItems(items);

        } catch (error) {
            console.error('예산 데이터 로드 실패:', error);
            message.error('예산 데이터를 불러오는데 실패했습니다');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) {
            loadBudgetData();
        }
    }, [projectId]);

    // 프로젝트 데이터 변경 시 예산 데이터 새로고침 (투입인력 변경 반영)
    useEffect(() => {
        if (projectId && projectData) {
            loadBudgetData();
        }
    }, [projectData]);

    // 예산 항목 변경 시 필터 재적용
    useEffect(() => {
        if (selectedCategory) {
            const filtered = budgetItems.filter(item => item.category === selectedCategory);
            setFilteredItems(filtered);
        } else {
            setFilteredItems(budgetItems);
        }
    }, [budgetItems, selectedCategory]);

    // 카테고리 필터링
    const handleCategoryFilter = (category) => {
        if (selectedCategory === category) {
            // 같은 카테고리 클릭시 필터 해제
            setSelectedCategory(null);
            setFilteredItems(budgetItems);
        } else {
            // 새 카테고리 선택
            setSelectedCategory(category);
            const filtered = budgetItems.filter(item => item.category === category);
            setFilteredItems(filtered);
        }
    };

    // 예산 항목 추가/수정
    const handleSaveBudgetItem = async (values) => {
        try {
            const token = localStorage.getItem('token');
            
            if (editingItem) {
                // 수정
                await axios.put(
                    `${API_BASE}/project-budget/items/${editingItem.id}`,
                    values,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                message.success('예산 항목이 수정되었습니다');
            } else {
                // 추가
                await axios.post(
                    `${API_BASE}/project-budget/project/${projectId}/items`,
                    values,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                message.success('예산 항목이 추가되었습니다');
            }
            
            setModalVisible(false);
            form.resetFields();
            setEditingItem(null);
            loadBudgetData();
            
        } catch (error) {
            message.error(error.response?.data?.message || '저장에 실패했습니다');
        }
    };

    // 예산 항목 삭제
    const handleDeleteBudgetItem = async (id) => {
        try {
            const token = localStorage.getItem('token');
            
            await axios.delete(
                `${API_BASE}/project-budget/items/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            message.success('예산 항목이 삭제되었습니다');
            loadBudgetData();
            
        } catch (error) {
            message.error(error.response?.data?.message || '삭제에 실패했습니다');
        }
    };

    // 모달 열기
    const openModal = (item = null) => {
        setEditingItem(item);
        setModalVisible(true);
        
        if (item) {
            form.setFieldsValue({
                category: item.category,
                vendor_name: item.vendor_name,
                description: item.description,
                budget_amount: item.budget_amount,
                actual_amount: item.actual_amount,
                notes: item.notes
            });
        } else {
            form.resetFields();
        }
    };

    // 테이블 컬럼 정의
    const columns = [
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (category) => {
                const config = categories[category];
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.name}
                    </Tag>
                );
            }
        },
        {
            title: '업체명',
            dataIndex: 'vendor_name',
            key: 'vendor_name',
            width: 120,
            render: (name) => name || '-'
        },
        {
            title: '내용',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: '예산금액',
            dataIndex: 'budget_amount',
            key: 'budget_amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {Math.round(amount || 0).toLocaleString()}원
                </Text>
            )
        },
        {
            title: '실제금액',
            dataIndex: 'actual_amount',
            key: 'actual_amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text style={{ color: '#52c41a' }}>
                    {Math.round(amount || 0).toLocaleString()}원
                </Text>
            )
        },
        {
            title: '집행률',
            dataIndex: 'execution_rate',
            key: 'execution_rate',
            width: 100,
            align: 'center',
            render: (rate) => (
                <Progress 
                    percent={rate} 
                    size="small" 
                    status={rate > 100 ? 'exception' : 'normal'}
                />
            )
        },
        {
            title: '작업',
            key: 'action',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openModal(record)}
                    />
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => {
                            Modal.confirm({
                                title: '예산 항목 삭제',
                                content: '이 예산 항목을 삭제하시겠습니까?',
                                onOk: () => handleDeleteBudgetItem(record.id)
                            });
                        }}
                    />
                </Space>
            )
        }
    ];

    return (
        <div>
            {/* 예산 요약 */}
            {budgetSummary && (
                <>
                    <Card size="small" title="예산 요약" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title="프로젝트 매출액"
                                    value={budgetSummary.budget_summary.contract_amount}
                                    suffix="원"
                                    formatter={(value) => `${Math.round(value).toLocaleString()}`}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="내부인건비"
                                    value={budgetSummary.budget_summary.internal_labor_cost}
                                    suffix="원"
                                    formatter={(value) => `${Math.round(value).toLocaleString()}`}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="외부비용"
                                    value={budgetSummary.budget_summary.external_budget}
                                    suffix="원"
                                    formatter={(value) => `${Math.round(value).toLocaleString()}`}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="총 예산"
                                    value={budgetSummary.budget_summary.total_budget}
                                    suffix="원"
                                    formatter={(value) => `${Math.round(value).toLocaleString()}`}
                                    valueStyle={{ color: '#fa541c' }}
                                />
                            </Col>
                        </Row>
                        
                        <Divider />
                        
                        {/* 카테고리별 요약 */}
                        <Row gutter={16}>
                            {Object.entries(budgetSummary.budget_by_category).map(([key, data]) => (
                                <Col span={6} key={key}>
                                    <Card 
                                        size="small" 
                                        hoverable
                                        style={{ 
                                            backgroundColor: selectedCategory === key ? '#e6f4ff' : '#fafafa',
                                            border: selectedCategory === key ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleCategoryFilter(key)}
                                    >
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <Text strong style={{ color: selectedCategory === key ? '#1890ff' : 'inherit' }}>
                                                {categories[key]?.icon} {data.name}
                                                {selectedCategory === key && ' ✓'}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                예산: {Math.round(data.total_budget).toLocaleString()}원
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                실제: {Math.round(data.total_actual).toLocaleString()}원
                                            </Text>
                                            <Progress 
                                                percent={data.execution_rate} 
                                                size="small"
                                                status={data.execution_rate > 100 ? 'exception' : 'normal'}
                                            />
                                        </Space>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </>
            )}

            {/* 예산 항목 관리 */}
            <Card
                size="small"
                title={
                    <Space>
                        <span>예산 항목 관리</span>
                        {selectedCategory && (
                            <Tag color={categories[selectedCategory]?.color} icon={categories[selectedCategory]?.icon}>
                                {categories[selectedCategory]?.name} 필터
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openModal()}
                    >
                        예산 항목 추가
                    </Button>
                }
            >
                <Table
                    dataSource={filteredItems}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    size="small"
                />
            </Card>

            {/* 예산 항목 추가/수정 모달 */}
            <Modal
                title={editingItem ? '예산 항목 수정' : '예산 항목 추가'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingItem(null);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveBudgetItem}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="구분"
                                rules={[{ required: true, message: '구분을 선택하세요' }]}
                            >
                                <Select placeholder="구분 선택">
                                    {Object.entries(categories).map(([key, config]) => (
                                        <Option key={key} value={key}>
                                            {config.icon} {config.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="vendor_name"
                                label="업체명"
                            >
                                <Input placeholder="업체명을 입력하세요" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="내용"
                        rules={[{ required: true, message: '내용을 입력하세요' }]}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="예산 항목의 상세 내용을 입력하세요"
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="budget_amount"
                                label="예산 금액"
                                rules={[{ required: true, message: '예산 금액을 입력하세요' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    placeholder="예산 금액"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="actual_amount"
                                label="실제 금액"
                                initialValue={0}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                    placeholder="실제 금액"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="notes"
                        label="비고"
                    >
                        <TextArea 
                            rows={2} 
                            placeholder="추가 메모나 비고사항"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => {
                                setModalVisible(false);
                                setEditingItem(null);
                                form.resetFields();
                            }}>
                                취소
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingItem ? '수정' : '추가'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default BudgetPlanningTab;